import { useMemo } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { DetailedReport } from '../../api/types';
import { Card } from '../ui/Card';
import {
  aggregatePaymentMethods,
  aggregateSalesByDay,
  CHART_COLORS,
} from '../../utils/report-charts';
import { formatCurrency } from '../../utils/format';
import './reports-charts.css';

interface ReportsChartsProps {
  reports: DetailedReport[];
}

function SalesTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ payload: { label: string; sales: number; tickets: number } }>;
}) {
  if (!active || !payload?.length) return null;
  const point = payload[0].payload;
  return (
    <div className="reports-chart-tooltip">
      <strong>{point.label}</strong>
      <span>Ventas: {formatCurrency(point.sales)}</span>
      <span>Tickets: {point.tickets}</span>
    </div>
  );
}

function PaymentTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ name: string; value: number }>;
}) {
  if (!active || !payload?.length) return null;
  const item = payload[0];
  return (
    <div className="reports-chart-tooltip">
      <strong>{item.name}</strong>
      <span>{formatCurrency(item.value)}</span>
    </div>
  );
}

export function ReportsCharts({ reports }: ReportsChartsProps) {
  const dailySales = useMemo(() => aggregateSalesByDay(reports), [reports]);
  const paymentMethods = useMemo(() => aggregatePaymentMethods(reports), [reports]);

  const paymentChartData = paymentMethods.map((item) => ({
    name: item.method,
    value: item.amount,
  }));

  return (
    <div className="grid-2 reports-charts" style={{ marginBottom: '1.5rem' }}>
      <Card title="Evolución de ventas" subtitle="Importe diario en el periodo seleccionado">
        <div className="reports-chart">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={dailySales} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fill: '#64748b', fontSize: 12 }}
                axisLine={{ stroke: '#dbe3ef' }}
                tickLine={false}
              />
              <YAxis
                tick={{ fill: '#64748b', fontSize: 12 }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(value: number) =>
                  new Intl.NumberFormat('es-ES', {
                    notation: 'compact',
                    maximumFractionDigits: 1,
                  }).format(value)
                }
              />
              <Tooltip content={<SalesTooltip />} cursor={{ fill: 'var(--color-primary-soft)' }} />
              <Bar dataKey="sales" name="Ventas" fill="var(--color-primary)" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card
        title="Métodos de pago"
        subtitle={
          paymentChartData.length > 0
            ? 'Desglose por importe cobrado'
            : 'Sin desglose disponible en este periodo'
        }
      >
        <div className="reports-chart">
          {paymentChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={paymentChartData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={58}
                  outerRadius={92}
                  paddingAngle={2}
                >
                  {paymentChartData.map((entry, index) => (
                    <Cell
                      key={entry.name}
                      fill={CHART_COLORS[index % CHART_COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip content={<PaymentTooltip />} />
                <Legend
                  verticalAlign="bottom"
                  iconType="circle"
                  formatter={(value: string) => (
                    <span className="reports-chart-legend">{value}</span>
                  )}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className="ui-empty">Los cierres no incluyen desglose por método de pago.</p>
          )}
        </div>
      </Card>
    </div>
  );
}
